import DiversityIcon from "@assets/icons/diversity.svg";
import DumbbellIcon from "@assets/icons/dumbbell.svg";
import FinishFlagIcon from "@assets/icons/finish-flag.svg";
import HelpIcon from "@assets/icons/care-giver.svg";
import SpeedometerIcon from "@assets/icons/speedometer.svg";
import { useI18n } from "@core/i18n/i18n";
import { Exercise, ExercisePatientWarning } from "@domain/exercise/exercise";
import { useExerciseFormatter } from "@ui/components/exercise/hooks/use-exercise-formatter";
import styled from "@emotion/styled";
import { row, stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { fonts } from "@ui/style/font";
import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { AlertCard, CardType } from "@ui/components/common/alert-card";

interface ExerciseDescriptionTabProps {
	exercise: Exercise;
}

export const ExerciseDescriptionTab: React.FC<ExerciseDescriptionTabProps> = props => {
	const { exercise } = props;
	const { format } = useI18n();
	const {
		formatExerciseCategory,
		formatEquipements,
		formatExerciseDifficulty,
		formatExerciseStartingPosition,
		formatExerciseCaregiverAssistance,
	} = useExerciseFormatter();

	return (
		<Content>
			<Section>
				{exercise.patientWarning === ExercisePatientWarning.AtaxiaWarning && (
					<AlertCard cardType={CardType.Warning} message={format("exerciseDetailsModal.ataxiaWarning")} />
				)}
			</Section>
			<Section>
				{exercise.description ? (
					<MarkdownWrapper>
						<ReactMarkdown remarkPlugins={[remarkGfm]}>{exercise.description}</ReactMarkdown>
					</MarkdownWrapper>
				) : (
					<SectionContent>{format("exerciseDetailsModal.emptySection")}</SectionContent>
				)}
			</Section>
			<Grid>
				<Section>
					<TopSection>
						<DiversityIcon width={24} height={24} color={colors.grey} />
						<div className="p-size-s-bold">{format("exercise.category.title")}</div>
					</TopSection>
					<SectionContent>{formatExerciseCategory(exercise.category)}</SectionContent>
				</Section>
				<Section>
					<TopSection>
						<DumbbellIcon width={24} height={24} color={colors.grey} />
						<div className="p-size-s-bold">{format("exerciseDetailsModal.mandatoryEquipment.title")}</div>
					</TopSection>
					<SectionContent>{formatEquipements(exercise.requiredEquipments)}</SectionContent>
				</Section>
				<Section>
					<TopSection>
						<DumbbellIcon width={24} height={24} color={colors.grey} />
						<div className="p-size-s-bold">{format("exerciseDetailsModal.optionalEquipment.title")}</div>
					</TopSection>
					<SectionContent>{formatEquipements(exercise.optionalEquipments)}</SectionContent>
				</Section>
				<Section>
					<TopSection>
						<FinishFlagIcon width={24} height={24} color={colors.grey} />
						<div className="p-size-s-bold">{format("exercise.startingPosition.title")}</div>
					</TopSection>
					<SectionContent>{formatExerciseStartingPosition(exercise.startingPosition)}</SectionContent>
				</Section>
				<Section>
					<TopSection>
						<HelpIcon width={24} height={24} color={colors.grey} />
						<div className="p-size-s-bold">{format("exercise.caregiverAssistance.title")}</div>
					</TopSection>
					<SectionContent>{formatExerciseCaregiverAssistance(exercise.helpNeeded)}</SectionContent>
				</Section>
				<Section>
					<TopSection>
						<SpeedometerIcon width={24} height={24} color={colors.grey} />
						<div className="p-size-s-bold">{format("exercise.difficulty.title")}</div>
					</TopSection>
					<SectionContent>{formatExerciseDifficulty(exercise.difficulty)}</SectionContent>
				</Section>
			</Grid>
		</Content>
	);
};

const Content = styled.div`
	${stack(8, "flexstart", "stretch")};
`;

const Section = styled.section`
	${stack(undefined, "flexstart", "stretch")};
`;

const TopSection = styled.div`
	${row(8, "flexstart", "center")}
`;

const SectionContent = styled.div`
	margin-left: 32px;
`;

const MarkdownWrapper = styled.div`
	p {
		margin: 8px 0;
	}

	ol {
		margin: 8px 0;
		padding-left: 24px;
		li {
			margin: 0;
			padding: 0px 0 16px 8px;
			::marker {
				font-family: ${fonts.header};
				font-weight: 500;
			}
		}
	}
`;

const Grid = styled.div`
	padding: 16px;
	border-radius: 8px;
	background-color: ${colors.primaryBackground};

	display: grid;
	grid-template-columns: 1fr 1fr;
	grid-gap: 16px;
`;
